import { useMemo } from 'react';
import type { Node as DiagramNode, Edge, NodeGroup } from '../types';
import { computeProxySockets, type ProxySocket } from '../components/nodes/GroupFrameComponent';

interface UseProxySocketMapOptions {
  groups: NodeGroup[];
  nodes: DiagramNode[];
  edges: Edge[];
}

interface ProxySocketMapResult {
  proxySocketMap: Map<string, ProxySocket[]>;
  proxySocketLookup: Map<string, { groupId: string; proxy: ProxySocket }>;
  collapsedChildIds: Set<string>;
}

export function useProxySocketMap({ groups, nodes, edges }: UseProxySocketMapOptions): ProxySocketMapResult {
  return useMemo(() => {
    // Non-frame groups always render collapsed (Blender style). Proxy sockets are
    // read from the persistent GroupInput/GroupOutput nodes — no edge scanning needed.
    const proxySocketMap = new Map<string, ProxySocket[]>();
    const proxySocketLookup = new Map<string, { groupId: string; proxy: ProxySocket }>();
    const collapsedChildIds = new Set<string>();
    const PROXY_W = 220;

    groups.forEach(group => {
      const treatAsCollapsed = group.isFrame ? group.collapsed : true;
      if (!treatAsCollapsed) return;
      group.childNodeIds.forEach(id => collapsedChildIds.add(id));

      if (!group.isFrame && (group.groupInputNodeId || group.groupOutputNodeId)) {
        const proxies: ProxySocket[] = [];
        let inIdx = 0;
        let outIdx = 0;

        if (group.groupInputNodeId) {
          const giNode = nodes.find(n => n.id === group.groupInputNodeId);
          if (giNode) {
            // GroupInput has paired sockets: gi-in-N (input, x:0) and gi-out-N (output, x:w).
            // External edges target gi-in-N — so proxy lookup must key off gi-in-N.
            giNode.sockets.filter(s => s.type === 'input').forEach(s => {
              const p: ProxySocket = {
                id: `proxy-in-${group.id}-${s.id}`,
                type: 'input',
                label: s.label ?? `In ${inIdx + 1}`,
                position: { x: 0, y: 40 + inIdx * 22 + 11 },
                originalNodeId: giNode.id,
                originalSocketId: s.id,
              };
              proxies.push(p);
              proxySocketLookup.set(`${giNode.id}:${s.id}`, { groupId: group.id, proxy: p });
              inIdx++;
            });
          }
        }

        if (group.groupOutputNodeId) {
          const goNode = nodes.find(n => n.id === group.groupOutputNodeId);
          if (goNode) {
            goNode.sockets.filter(s => s.type === 'output').forEach(s => {
              const p: ProxySocket = {
                id: `proxy-out-${group.id}-${s.id}`,
                type: 'output',
                label: s.label ?? `Out ${outIdx + 1}`,
                position: { x: PROXY_W, y: 40 + outIdx * 22 + 11 },
                originalNodeId: goNode.id,
                originalSocketId: s.id,
              };
              proxies.push(p);
              proxySocketLookup.set(`${goNode.id}:${s.id}`, { groupId: group.id, proxy: p });
              outIdx++;
            });
          }
        }

        proxySocketMap.set(group.id, proxies);
      } else {
        const proxies = computeProxySockets(group, nodes, edges);
        proxySocketMap.set(group.id, proxies);
        proxies.forEach(ps => {
          proxySocketLookup.set(`${ps.originalNodeId}:${ps.originalSocketId}`, { groupId: group.id, proxy: ps });
        });
      }
    });

    return { proxySocketMap, proxySocketLookup, collapsedChildIds };
  }, [groups, nodes, edges]);
}
